/**
 * Oarose Heritage - Premium Luxury Suite Scripts
 * Elegant animations, responsive cursor halos, scroll-reveals, and custom load experience.
 * Incorporates dynamic theme customization switches.
 */

document.addEventListener('DOMContentLoaded', () => {
  // Read dynamic settings configured via the Shopify Theme Customizer
  const config = window.OarosePremiumConfig || {
    enableScrollReveal: true,
    enableCustomCursor: true,
    enableImageZoom: true
  };

  // --- 1. Luxury Welcome Splash Screen (sessionStorage) ---
  const loader = document.getElementById('oarose-loader');
  const progressFill = document.querySelector('.loader-progress-fill');
  
  const hasVisited = sessionStorage.getItem('oarose_visited');
  
  if (hasVisited && loader) {
    // Already visited in this session: bypass loader instantly for seamless navigation
    loader.style.display = 'none';
    loader.classList.add('loader-hidden');
    // Ensure all sections are active immediately if loader bypassed
    document.querySelectorAll('.shopify-section').forEach(section => {
      section.classList.add('reveal-active');
    });
    
    // Post-loader initialization
    if (config.enableScrollReveal) {
      initializeScrollReveal();
    } else {
      document.querySelectorAll('.shopify-section').forEach(section => {
        section.classList.add('reveal-active');
      });
    }
  } else if (loader) {
    // First time visiting this session: show luxury introduction animation
    sessionStorage.setItem('oarose_visited', 'true');
    
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += (100 - progress) * 0.2; // Smooth decelerating loading bar
      if (progressFill) {
        progressFill.style.width = `${Math.min(progress, 98)}%`;
      }
    }, 80);

    const finishLoading = () => {
      clearInterval(progressInterval);
      if (progressFill) progressFill.style.width = '100%';
      
      setTimeout(() => {
        loader.classList.add('loader-hidden');
        // Trigger reveal animations for initially visible sections shortly after loader starts fading
        setTimeout(() => {
          loader.style.display = 'none';
          
          if (config.enableScrollReveal) {
            initializeScrollReveal();
          } else {
            document.querySelectorAll('.shopify-section').forEach(section => {
              section.classList.add('reveal-active');
            });
          }
        }, 800);
      }, 500);
    };

    // Fade out loader once the window asset load event fires, or after 1.2s maximum fallback
    const loaderSafetyTimeout = setTimeout(finishLoading, 1200);

    window.addEventListener('load', () => {
      clearTimeout(loaderSafetyTimeout);
      finishLoading();
    });
  } else {
    // Fallback: initialize scroll reveal directly if loader doesn't exist/disabled
    if (config.enableScrollReveal) {
      initializeScrollReveal();
    } else {
      document.querySelectorAll('.shopify-section').forEach(section => {
        section.classList.add('reveal-active');
      });
    }
  }

  // --- 2. Scroll-Driven Reveal Animations ---
  function initializeScrollReveal() {
    const elements = document.querySelectorAll('.shopify-section, .reveal-on-scroll');
    
    // Core IntersectionObserver config
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -8% 0px', // Trigger slightly before scrolling onto screen
      threshold: 0.05
    };

    const elementObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-active');
          observer.unobserve(entry.target); // Animate once, keep it on screen
        }
      });
    }, observerOptions);

    elements.forEach(element => {
      // Determine if element is already in or near viewport on start (or header/footer groups)
      const rect = element.getBoundingClientRect();
      const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (inViewport || element.closest('.shopify-section-header-group') || element.closest('.shopify-section-footer-group')) {
        element.classList.add('reveal-active');
      } else {
        elementObserver.observe(element);
      }
    });

    // Make sure dynamically loaded Shopify sections (Theme Editor customize updates) get activated
    document.addEventListener('shopify:section:load', (event) => {
      event.target.classList.add('reveal-active');
      const innerReveals = event.target.querySelectorAll('.reveal-on-scroll');
      innerReveals.forEach(el => el.classList.add('reveal-active'));
    });
  }

  // --- 3. Interactive Desktop Custom Cursor Glow & Magnetic Hover ---
  const initCursor = () => {
    // Check if cursor halo is enabled via Shopify Admin
    if (!config.enableCustomCursor) return;
    
    // Only target fine pointers (mouse users / desktop) to ensure excellent mobile UX
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const dot = document.createElement('div');
    const halo = document.createElement('div');
    
    dot.className = 'oarose-cursor-dot';
    halo.className = 'oarose-cursor-halo';
    
    document.body.appendChild(dot);
    document.body.appendChild(halo);

    let mouseX = 0, mouseY = 0; // Mouse coords
    let haloX = 0, haloY = 0;   // Halo coords (smoothed)
    let isHidden = true;

    // Track mouse coordinates
    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      
      if (isHidden) {
        dot.style.opacity = '1';
        halo.style.opacity = '1';
        isHidden = false;
      }
    });

    // Handle mouse leaving and entering viewport
    document.addEventListener('mouseleave', () => {
      dot.style.opacity = '0';
      halo.style.opacity = '0';
      isHidden = true;
    });

    document.addEventListener('mouseenter', () => {
      dot.style.opacity = '1';
      halo.style.opacity = '1';
      isHidden = false;
    });

    // Buttery-smooth Linear Interpolation (LERP) loop for elegant halo lag
    const renderCursor = () => {
      // Simple physics lag: move 14% of the distance each frame
      haloX += (mouseX - haloX) * 0.14;
      haloY += (mouseY - haloY) * 0.14;
      
      dot.style.left = `${mouseX}px`;
      dot.style.top = `${mouseY}px`;
      
      halo.style.left = `${haloX}px`;
      halo.style.top = `${haloY}px`;
      
      requestAnimationFrame(renderCursor);
    };
    
    requestAnimationFrame(renderCursor);

    // Dynamic Hover States for Interactive Elements
    const hoverSelector = 'a, button, select, input, textarea, [role="button"], .oarose-hover-zoom, .interactive-cursor, summary';
    
    document.addEventListener('mouseover', (e) => {
      if (e.target.closest(hoverSelector)) {
        document.body.classList.add('cursor-hovering');
      }
    });
    
    document.addEventListener('mouseout', (e) => {
      if (e.target.closest(hoverSelector) && !e.relatedTarget?.closest(hoverSelector)) {
        document.body.classList.remove('cursor-hovering');
      }
    });

    // Clicking / Ripple interaction
    document.addEventListener('mousedown', () => {
      document.body.classList.add('cursor-clicking');
    });

    document.addEventListener('mouseup', () => {
      document.body.classList.remove('cursor-clicking');
    });
  };

  // Safe initiation of desktop custom cursor
  initCursor();

  // --- 4. Floating Branded "Back to Top" Button ---
  const bttButton = document.getElementById('oarose-back-to-top');
  
  if (bttButton) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 400) {
        bttButton.classList.add('btt-visible');
      } else {
        bttButton.classList.remove('btt-visible');
      }
    }, { passive: true });

    bttButton.addEventListener('click', (e) => {
      e.preventDefault();
      
      // Add a temporary ultra-smooth glide animation class to the custom cursor
      document.body.classList.add('cursor-hovering');
      setTimeout(() => document.body.classList.remove('cursor-hovering'), 800);

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  // --- 5. Custom Premium Image Zoom Card Integration ---
  if (config.enableImageZoom) {
    // We automatically attach the custom zoom wrappers to key images if they aren't pre-configured
    const gridImages = document.querySelectorAll('.heritage-split__image-container img, .hero-split__image-container img, .collection-grid__image-container img');
    gridImages.forEach(img => {
      const parent = img.parentElement;
      if (parent && !parent.classList.contains('oarose-hover-zoom')) {
        parent.classList.add('oarose-hover-zoom');
      }
    });
  }

  // --- 6. FAQ Accordion Height Animation & Chevron Rotation ---
  document.querySelectorAll('details.group').forEach(details => {
    const summary = details.querySelector('summary');
    const content = details.querySelector('div, .faq-answer');
    const chevron = details.querySelector('.chevron-icon, .expand-icon');
    
    if (!summary || !content) return;

    let isAnimating = false;
    
    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (isAnimating) return;
      
      const isOpen = details.hasAttribute('open');
      
      if (isOpen) {
        // CLOSE / COLLAPSE
        isAnimating = true;
        const startHeight = details.offsetHeight;
        const endHeight = summary.offsetHeight;
        
        details.style.height = `${startHeight}px`;
        details.offsetHeight; // force repaint
        
        details.style.height = `${endHeight}px`;
        if (chevron) {
          chevron.style.transform = 'rotate(0deg)';
        }
        
        setTimeout(() => {
          details.removeAttribute('open');
          details.style.height = '';
          isAnimating = false;
        }, 300);
      } else {
        // OPEN / EXPAND
        isAnimating = true;
        details.setAttribute('open', 'true');
        
        const startHeight = summary.offsetHeight;
        const endHeight = summary.offsetHeight + content.offsetHeight;
        
        details.style.height = `${startHeight}px`;
        details.offsetHeight; // force repaint
        
        details.style.height = `${endHeight}px`;
        if (chevron) {
          chevron.style.transform = 'rotate(180deg)';
        }
        
        setTimeout(() => {
          details.style.height = '';
          isAnimating = false;
        }, 300);
      }
    });
  });
});
